import axios from "axios";
import Cookies from "js-cookie";
import { checkForAuthorization } from "utils/index";

// Which app the interaction came from. Startup-side analytics counts investor-web
// and backrr-main together, but keeps them distinguishable.
const ENGAGEMENT_SOURCE = "investor_web";

// Mirrors src/helpers/viewTypes.ts in investor-backend.
export const VIEW_TYPES = {
    STARTUP_PROFILE: "startup_profile",
    PITCH_REEL: "pitch_reel",
    PRODUCT_VIDEO: "product_video",
    DECK_VIEW: "deck_view",
    CONTACT_VIEW: "contact_view",
    DOCUMENT_VIEW: "document_view",
    DATA_ROOM: "data_room",
    PITCH_DECK_PAGE: "pitch_deck_page",
};

export const CONTACT_TARGETS = {
    WEBSITE: "website",
    EMAIL: "email",
    LINKEDIN: "linkedin",
    FOUNDER_LINKEDIN: "founder_linkedin",
};

const engagementUrl = (path) =>
    `${process.env.REACT_APP_BASE_URL}/engagement/${path}`;

// Admins browsing on a founder's behalf would otherwise show up as investor
// interest. Matches the check in useInvestorViewTracking.
export const isAdminImpersonating = () =>
    Boolean(Cookies.get("X-ADMIN-ID") && Cookies.get("X-ADMIN-JWT"));

// Tracking is invisible plumbing - a failure here must never reach the investor,
// so every call swallows its own errors.
const post = async (path, payload) => {
    try {
        await axios({
            method: "post",
            url: engagementUrl(path),
            headers: { Authorization: `Bearer ${checkForAuthorization()}` },
            data: payload,
        });
    } catch (error) {
        // Intentionally silent.
    }
};

export const trackEngagementEvent = ({
    startup_id,
    investor_id,
    view_type,
    target = "",
}) => {
    if (!startup_id || !investor_id || isAdminImpersonating()) return;

    return post("event", {
        startup_id,
        investor_id,
        view_type,
        target,
        source: ENGAGEMENT_SOURCE,
    });
};

export const startViewSession = ({
    session_id,
    startup_id,
    investor_id,
    view_type = VIEW_TYPES.STARTUP_PROFILE,
}) => {
    if (!session_id || !startup_id || !investor_id) return;

    return post("session/start", {
        session_id,
        startup_id,
        investor_id,
        view_type,
        source: ENGAGEMENT_SOURCE,
    });
};

// `keepalive` rather than navigator.sendBeacon: the endpoint needs an
// Authorization header, and sendBeacon cannot set one.
export const sendSessionHeartbeat = ({
    session_id,
    investor_id,
    active_seconds,
    keepalive = false,
}) => {
    if (!session_id || !investor_id) return;

    const body = {
        session_id,
        investor_id,
        active_seconds: Math.max(0, Math.floor(active_seconds)),
    };

    if (!keepalive) return post("session/heartbeat", body);

    try {
        return fetch(engagementUrl("session/heartbeat"), {
            method: "POST",
            keepalive: true,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${checkForAuthorization()}`,
            },
            body: JSON.stringify(body),
        }).catch(() => { });
    } catch (error) {
        // Intentionally silent.
    }
};
